/* eslint-disable @typescript-eslint/consistent-type-imports */
import type ConversationAssistant from '@atlassian/conversation-assistant';
import type { ConversationAction } from '@atlassian/rovo-conversation-actions';
import type { ConversationActionTags } from '@atlassian/rovo-conversation-actions/types';

export type RovoAction = {
	action: ConversationAction;
};

export type CustomGreetingConfig = {
	iconSrc?: string;
	iconSrcDark?: string;
	title?: string;
	subtitle?: string;
	truncateGreeting?: boolean;
};

export type RovoConfiguration = {
	getActions: React.ComponentProps<typeof ConversationAssistant>['getActions'];
	tags?: ConversationActionTags;
	nudges?: ConversationAction[];
	customGreeting?: CustomGreetingConfig;
	/**
	 * Controls whether the greeting is displayed in the empty state.
	 * When true, the greeting is always shown regardless of mode.
	 * When false, the greeting is always hidden regardless of mode.
	 * When undefined, the default behavior applies (hidden in mini modal, shown elsewhere).
	 * @default undefined
	 */
	shouldShowGreeting?: boolean;
	/**
	 * When true, only the actions fetched from the custom getActions provider are displayed,
	 * rather than being merged with the default actions.
	 * @default undefined
	 */
	prioritizeCustomActionResults?: boolean;
	/**
	 * Controls the maximum number of conversation starters shown before the platform
	 * stops backfilling with additional dynamic starters.
	 * @default undefined (falls back to platform default of 3)
	 */
	numStartersToShow?: number;
};

export interface RovoConfigurationProviderProps {
	setConfiguration: (config: RovoConfiguration) => void;
}

export enum RegisteredExperience {
	VIEW_PAGE = 'view-page',
	EDIT_PAGE = 'edit-page',
	WHITEBOARD = 'whiteboard',
	SEARCH_PAGE = 'search-page',
	HOME = 'home',
	FOLDER = 'folder',
}

export type RegisteredExperienceType = RegisteredExperience;

export type RovoProviderComponent = React.ComponentType<any>;

export type RovoConfigProviderBaseProps = {
	experience?: RegisteredExperienceType;
	provider?: RovoProviderComponent;
};

export type CommonRovoConfigProviderProps = {
	contentId: string;
	spaceKey: string;
	contentTitle: string;
};

export type PageRovoConfigProviderProps<ContributorsRef = unknown> = RovoConfigProviderBaseProps &
	CommonRovoConfigProviderProps & {
		contentType: string;
		contributorsFragmentRef?: ContributorsRef;
		lastModifiedDate?: string | null;
		isUnpublishedDraft?: boolean;
		readTime?: number;
		readTimeLoading?: boolean;
		isEditorEmpty?: boolean;
	};

export type WhiteboardRovoConfigProviderProps = RovoConfigProviderBaseProps &
	CommonRovoConfigProviderProps & {
		actionsToRender: ConversationAction[];
	};

export type FolderRovoConfigProviderProps = RovoConfigProviderBaseProps &
	CommonRovoConfigProviderProps & {
		actionsToRender: ConversationAction[];
	};

export type SearchPageRovoConfigProviderProps = RovoConfigProviderBaseProps & {
	getSearchQuery?: () => string | undefined;
	shouldUseSearchPageRovoConfig?: boolean;
	shouldShowFSJNudge?: boolean;
};

export type HomeRovoConfigProviderProps = RovoConfigProviderBaseProps;

export type RovoConfigProviderProps =
	| PageRovoConfigProviderProps
	| WhiteboardRovoConfigProviderProps
	| SearchPageRovoConfigProviderProps
	| HomeRovoConfigProviderProps
	| FolderRovoConfigProviderProps;

// Same shape as WhiteboardRovoConfigProviderProps, so this check is identical to
// isWhiteboardRovoConfigProviderProps below — RovoConfigProvider only calls each guard
// after already dispatching on `props.experience`, so the guards never need to
// distinguish experiences from each other, only confirm the required fields are present.
export const isWhiteboardRovoConfigProviderProps = (
	props: RovoConfigProviderProps,
): props is WhiteboardRovoConfigProviderProps => 'actionsToRender' in props;

export const isFolderRovoConfigProviderProps = (
	props: RovoConfigProviderProps,
): props is FolderRovoConfigProviderProps => 'actionsToRender' in props;

export const isPageRovoConfigProviderProps = (
	props: RovoConfigProviderProps,
): props is PageRovoConfigProviderProps => 'contentType' in props;

export const isSearchPageRovoConfigProviderProps = (
	props: RovoConfigProviderProps,
): props is SearchPageRovoConfigProviderProps => 'getSearchQuery' in props;
